import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Table, TableBody, TableContainer, TableRow } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CompanySmall } from 'api';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { EMPTY_VALUATIONS_TABLE } from 'common/constants/documents';
import { AttioSortDirection } from 'dashboard409a/common/constants/analyst-dashboard';
import { columns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { JobFilters } from 'dashboard409a/pages/Workboard/Filters';
import { getValuationStatusOptions } from 'dashboard409a/services/attio-common';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import { handleValuationRowClick } from 'dashboard409a/utilities';
import { StyledTableCellEmptyMessage } from './common';
import { EnhancedTableHeadColumn } from './ValuationsTable.types';
import ValuationsTableHead from './ValuationsTableHead';
import ValuationsTableRow from './ValuationsTableRow';

interface ValuationsTableBulkDataProps {
  isLoading: boolean;
  rows: SimplifiedAttioValuation[];
  onStatusUpdate: (recordId: string, newStatus: string) => void;
  onNoteUpdate: (recordId: string, newNotes: string) => void;
  onNoteSave: (valuationId: string, noteContent: string, currentNote?: string) => Promise<void>;
  orderBy: string;
  order: AttioSortDirection;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  isNoteLoading?: boolean;
  noteError?: string | null;
}

export const ValuationsTableBulkData = ({
  isLoading,
  rows,
  onStatusUpdate,
  onNoteUpdate,
  onNoteSave,
  orderBy,
  order,
  onRequestSort,
  isNoteLoading = false,
  noteError = null,
}: ValuationsTableBulkDataProps) => {
  const navigate = useNavigate();

  const allRows = rows;

  const [filteredRows, setFilteredRows] = useState<SimplifiedAttioValuation[]>(allRows);

  useEffect(() => {
    setFilteredRows(allRows);
  }, [allRows]);

  const applyFilters = useCallback((filterState: {
      searchParam: string;
      searchStatus: string;
      fromDate: Date | null;
      toDate: Date | null;
    }) => {
    let filtered = [...allRows];
  
    if (filterState.searchParam.trim()) {
      const searchLower = filterState.searchParam.toLowerCase();
      filtered = filtered.filter(row => 
        row.company_name?.toLowerCase().includes(searchLower) ||
          row.valuation_fee?.toLowerCase().includes(searchLower) ||
          row.record_id?.toLowerCase().includes(searchLower)
      );
    }
  
    if (filterState.searchStatus !== 'default') {
      filtered = filtered.filter(row => row.status === filterState.searchStatus);
    }
  
    if (filterState.fromDate) {
      filtered = filtered.filter(row => {
        if (!row.valuation_date) {
          return false;
        }
        const fromDateEndOfDay = new Date(filterState.fromDate!);
        fromDateEndOfDay.setHours(23, 59, 59, 999);
        const rowDate = new Date(row.valuation_date);
        rowDate.setHours(23, 59, 59, 999);

        return rowDate >= fromDateEndOfDay;
      });
    }
  
    if (filterState.toDate) {
      filtered = filtered.filter(row => {
        if (!row.valuation_date) {
          return false;
        }
        const rowDate = new Date(row.valuation_date);
        const toDateEndOfDay = new Date(filterState.toDate!);
        rowDate.setHours(23, 59, 59, 999);
        toDateEndOfDay.setHours(23, 59, 59, 999);
        return rowDate < toDateEndOfDay;
      });
    }
  
    setFilteredRows(filtered);
  }, [allRows]);
  
  const { data: attioStatusOptions = [], isLoading: isLoadingStatusOptions } = useQuery({
    queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
    queryFn: getValuationStatusOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const statusOptions = attioStatusOptions.map(option => ({
    id: {
      workspace_id: option.id.workspace_id,
      object_id: option.id.object_id,
      attribute_id: option.id.attribute_id,
      option_id: option.id.option_id,
    },
    title: option.title,
    is_archived: option.is_archived,
  }));
  const filterStatusOptions = statusOptions.filter(option => rows.some(row => row.status === option.title));
  const statusOptionsLoading = isLoadingStatusOptions;
  const user = useSelector((state: RootStateType) => state.user);
  const standaloneCompanies = useMemo(() => {
    if (user) {
      return user.standalone_companies_permissions as unknown as CompanySmall[];
    }
    return [];
  }, [user]);

  const handleRowClick = (row: SimplifiedAttioValuation, event: React.MouseEvent<HTMLTableRowElement>) => {
    // Use the shared navigation utility
    handleValuationRowClick(row, event, navigate, standaloneCompanies);
  };
  
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <JobFilters 
        statusOptions={filterStatusOptions} 
        statusOptionsLoading={statusOptionsLoading} 
        onFiltersChange={applyFilters}
      />
      <TableContainer>
        <Table>
          <ValuationsTableHead
            orderBy={orderBy}
            order={order}
            onRequestSort={onRequestSort}
            columns={columns as EnhancedTableHeadColumn[]}
          />
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow style={{ height: 33 }}>
                <StyledTableCellEmptyMessage colSpan={6}>{EMPTY_VALUATIONS_TABLE}</StyledTableCellEmptyMessage>
              </TableRow>
            ) : (
              filteredRows.map(row => (
                <ValuationsTableRow
                  key={row.record_id}
                  valuationRow={row}
                  onRowClick={handleRowClick}
                  onStatusUpdate={updatedRow => onStatusUpdate(updatedRow.record_id, updatedRow.status || '')}
                  onNoteUpdate={updatedRow => onNoteUpdate(updatedRow.record_id, updatedRow.note?.content || '')}
                  onNoteSave={noteContent => {
                    const currentNoteContent = row.note?.content || '';
                    return onNoteSave(row.record_id, noteContent, currentNoteContent);
                  }}
                  statusOptions={statusOptions}
                  statusOptionsLoading={statusOptionsLoading}
                  isNoteLoading={isNoteLoading}
                  noteError={noteError}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
